/**
 * Phase 2 Feature H — symptom-centric lookup grouped by temporal bucket.
 */

import { TEMPORAL_BUCKETS, FEATURE_TYPES } from './ddxTemporalTaxonomy.js';
import { temporalFitScore } from './ddxKbProjection.js';

const BUCKET_ORDER = [
  TEMPORAL_BUCKETS.HYPERACUTE,
  TEMPORAL_BUCKETS.ACUTE,
  TEMPORAL_BUCKETS.SUBACUTE,
  TEMPORAL_BUCKETS.CHRONIC,
  TEMPORAL_BUCKETS.INDETERMINATE,
];

/**
 * @param {string} featureId
 * @param {{ bucket?: string, onset?: string | null }} temporalContext
 * @param {{ edges: object[], getDiagnosis?: (id: string) => object | null, cantMissDxIds?: string[] }} graph
 * @returns {object}
 */
export function generateSymptomLookup(featureId, temporalContext, graph) {
  const acuity = temporalContext?.bucket || TEMPORAL_BUCKETS.INDETERMINATE;
  const edges = (graph?.edges || []).filter((e) => e.featureId === featureId && e.direction !== 'contradicts');
  /** @type {Record<string, object[]>} */
  const byBucket = {};
  for (const b of BUCKET_ORDER) byBucket[b] = [];

  for (const e of edges) {
    const buckets = Array.isArray(e.temporalBuckets) && e.temporalBuckets.length ? e.temporalBuckets : [TEMPORAL_BUCKETS.INDETERMINATE];
    for (const b of buckets) {
      if (!byBucket[b]) byBucket[b] = [];
      byBucket[b].push(e);
    }
  }

  const sections = [];
  for (const b of BUCKET_ORDER) {
    const list = byBucket[b] || [];
    if (list.length === 0) continue;
    const diagnosisIds = [...new Set(list.map((x) => x.diagnosisId).filter(Boolean))];
    const conditions = diagnosisIds.map((id) => {
      const d = graph.getDiagnosis ? graph.getDiagnosis(id) : { label: id };
      return d?.label || d?.name || id;
    });
    sections.push({
      temporalBucket: b,
      fitToPatient: temporalFitScore(acuity, list[0]?.temporalBuckets),
      conditions,
      historyToAsk: list
        .filter((e) => String(e.featureType || '').includes('history'))
        .map((e) => e.historyPrompt || e.clinicalPearl || e.featureId)
        .filter(Boolean)
        .slice(0, 12),
      examToPerform: list
        .filter((e) =>
          [FEATURE_TYPES.EXAM_GENERAL, FEATURE_TYPES.EXAM_FOCUSED, FEATURE_TYPES.EXAM_VITAL].includes(
            e.featureType
          )
        )
        .map((e) => e.examPrompt || e.featureId),
      diagnostics: list
        .filter((e) => String(e.featureType || '').startsWith('diagnostic'))
        .map((e) => e.featureId),
      temporalDiscriminators: list
        .filter((e) => e.clinicalSignificance === 'discriminator')
        .map((e) => e.discriminatorNote || e.notes)
        .filter(Boolean)
        .slice(0, 8),
      cantMissStillRequired: diagnosisIds.filter((id) => (graph.cantMissDxIds || []).includes(id)),
    });
  }

  return {
    featureId,
    temporalContext,
    sections,
  };
}
